import { AnimatePresence } from "framer-motion";
import { ImageUp, Plus, Search, Trash2, UserPlus, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Character, Creative, Hypothesis } from "@lovmia/shared";
import { Button, CharacterAvatar, Lightbox, Modal, Select } from "./Ui";
import { useAppState, useCharacterUpload } from "../state";

/**
 * Кастинг гипотезы (GEN-1192).
 *
 * Персонаж виден на доске до того, как появилось хоть одно видео: кого мы
 * собираемся снимать — такое же решение, как боль и угол, и принимается оно
 * раньше продакшна. Библиотека общая на воркспейс, поэтому здесь только
 * привязка, а сам персонаж заводится один раз.
 */

/** Кто может трогать кастинг: то же производственное право, что и на заливку файла. */
function canCast(role: string) {
  return ["admin", "user"].includes(role);
}

/** Аватарки без действий — для карточек на канбане. */
export function CastingStrip({ characters, limit = 4, onOpen }: {
  characters: Character[];
  limit?: number;
  onOpen?: (character: Character) => void;
}) {
  if (!characters.length) return null;
  const shown = characters.slice(0, limit);
  const rest = characters.length - shown.length;
  return <div className="casting-strip">
    {shown.map((character) => onOpen
      ? <button
        key={character.id}
        type="button"
        className="casting-strip__item"
        title={character.name}
        onClick={(event) => { event.stopPropagation(); onOpen(character); }}
      ><CharacterAvatar character={character} size="sm" /></button>
      : <span key={character.id} className="casting-strip__item" title={character.name}>
        <CharacterAvatar character={character} size="sm" />
      </span>)}
    {rest > 0 && <span className="casting-strip__rest">+{rest}</span>}
  </div>;
}

/** Форма нового персонажа: имя, описание кастинга и лист одним шагом. */
function CreateCharacter({ onCreated, onCancel }: {
  onCreated: (character: Character) => Promise<void> | void;
  onCancel: () => void;
}) {
  const { act, notify } = useAppState();
  const { upload, label: uploadLabel } = useCharacterUpload();
  const [form, setForm] = useState({ name: "", description: "" });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const character = await act<Character>("/api/characters", {
        method: "POST",
        body: JSON.stringify({ name: form.name.trim(), description: form.description.trim() || null })
      });
      // Лист догружаем вторым шагом: персонаж уже существует, и упавшая
      // загрузка не должна стирать заведённую карточку — её достаточно
      // дозаполнить, а не заводить заново.
      if (file && !await upload(character.id, file)) {
        notify("Персонаж создан, но лист не загрузился — попробуйте ещё раз", undefined, "danger");
      }
      await onCreated(character);
    } finally {
      setBusy(false);
    }
  };

  return <form className="casting-create" onSubmit={submit}>
    <div className="casting-create__grid">
      <label className="field"><span>Имя</span>
        <input autoFocus required minLength={2} maxLength={80} value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Мия с фермы" />
      </label>
      <button type="button" className="casting-create__drop" onClick={() => input.current?.click()}>
        {preview ? <img src={preview} alt="" /> : <><ImageUp size={18} /><span>Character sheet</span></>}
        <input ref={input} hidden type="file" accept="image/jpeg,image/png,image/webp"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
      </button>
    </div>
    <label className="field field--full"><span>Кастинг словами</span>
      <textarea maxLength={1000} value={form.description}
        onChange={(event) => setForm({ ...form, description: event.target.value })}
        placeholder={"Взрослая, 28. Телосложение, волосы, гардероб по предметам, вайб, голос.\nЭто описание уезжает в промпт листа и сегментов."} />
    </label>
    <div className="dialog-actions">
      <Button type="button" variant="ghost" onClick={onCancel}>Отмена</Button>
      <Button type="submit" variant="primary" loading={busy}>{busy ? uploadLabel ?? "Создаём…" : "Создать"}</Button>
    </div>
  </form>;
}

/** Выбор из библиотеки или создание нового. */
function CastingPicker({ family, close }: { family: Hypothesis; close: () => void }) {
  const { data, act } = useAppState();
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const linked = new Set(family.characters.map((character) => character.id));
  const link = async (characterId: string) => {
    setBusy(characterId);
    try { await act(`/api/hypotheses/${family.id}/characters`, { method: "POST", body: JSON.stringify({ characterId }) }, "Персонаж в кастинге"); }
    finally { setBusy(null); }
  };
  // Выведенные из оборота не предлагаем: библиотека — это то, кем можно
  // снимать сейчас, а история кастинга живёт в самих гипотезах.
  const available = data.characters.filter((character) => {
    if (linked.has(character.id) || character.status !== "active") return false;
    const needle = query.trim().toLowerCase();
    return !needle || `${character.name} ${character.description ?? ""}`.toLowerCase().includes(needle);
  });

  return <Modal onClose={close}><div className="dialog-form casting-picker">
    <span className="eyebrow">Кастинг · {family.id}</span>
    <h2>Кто снимается</h2>
    <p>Персонаж живёт в библиотеке воркспейса: лист генерится один раз и переиспользуется в разных гипотезах.</p>
    {/* Выбор из списка оставляет модалку открытой: привязанный уходит из
        списка, и это само по себе ответ, а пару кастингов набирают подряд.
        После создания нового закрываем — форма отработала, и человек должен
        увидеть карточку, а не пустеющий список за ней. */}
    {creating
      ? <CreateCharacter onCancel={() => setCreating(false)} onCreated={async (character) => { await link(character.id); close(); }} />
      : <>
        <div className="casting-picker__head">
          <div className="search-box">
            <Search size={15} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Имя или описание" />
            {query && <button type="button" onClick={() => setQuery("")} aria-label="Очистить"><X size={13} /></button>}
          </div>
          <Button variant="primary" onClick={() => setCreating(true)}><Plus size={15} /> Новый персонаж</Button>
        </div>
        <div className="casting-picker__list">
          {available.map((character) => <button
            key={character.id}
            type="button"
            className="casting-option"
            disabled={busy === character.id}
            onClick={() => link(character.id)}
          >
            <CharacterAvatar character={character} size="md" />
            <div>
              <strong>{character.name}</strong>
              {character.description && <p>{character.description}</p>}
              <small>{character.image ? "лист загружен" : "листа ещё нет"}{character.hypothesisIds.length ? ` · снимается в ${character.hypothesisIds.length}` : ""}</small>
            </div>
            <UserPlus size={16} />
          </button>)}
          {!available.length && <p className="script-empty">
            {data.characters.length ? "Все подходящие персонажи уже в кастинге этой гипотезы." : "Библиотека пуста — заведите первого персонажа."}
          </p>}
        </div>
      </>}
  </div></Modal>;
}

/** Блок кастинга в карточке гипотезы. */
export function HypothesisCasting({ family }: { family: Hypothesis }) {
  const { data, act } = useAppState();
  const [picking, setPicking] = useState(false);
  const [opened, setOpened] = useState<Character | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const editable = canCast(data.user.role);
  const unlink = async (character: Character) => {
    setBusy(character.id);
    try { await act(`/api/hypotheses/${family.id}/characters/${character.id}`, { method: "DELETE" }, `${character.name} снят с гипотезы`); }
    finally { setBusy(null); }
  };
  return <section className="casting">
    <header>
      <div><span>Кастинг</span><small>кто снимается в крео этой гипотезы</small></div>
      {editable && <Button onClick={() => setPicking(true)}><UserPlus size={15} /> Прикрепить</Button>}
    </header>
    {family.characters.length ? <div className="casting-list">
      {family.characters.map((character) => <article className="casting-card" key={character.id}>
        <button type="button" className="casting-card__face" onClick={() => setOpened(character)}
          aria-label={`Открыть лист персонажа ${character.name}`}>
          <CharacterAvatar character={character} size="lg" />
        </button>
        <div className="casting-card__body">
          <strong>{character.name}</strong>
          {character.description && <p>{character.description}</p>}
          {!character.image && <small>лист ещё не загружен</small>}
          {character.status === "retired" && <small>выведен из оборота</small>}
        </div>
        {editable && <button
          type="button"
          className="casting-card__remove"
          disabled={busy === character.id}
          onClick={() => unlink(character)}
          aria-label={`Снять ${character.name} с гипотезы`}
        ><Trash2 size={14} /></button>}
      </article>)}
    </div> : <p className="script-empty">
      Персонажей пока нет. Прикрепите кастинг до продакшна — тогда на доске видно, кто снимается, ещё до первого ролика.
    </p>}
    <AnimatePresence>
      {picking && <CastingPicker family={family} close={() => setPicking(false)} />}
      {opened?.image && <Lightbox
        src={opened.image.url}
        alt={`Character sheet ${opened.name}`}
        title={opened.name}
        caption={opened.description}
        onClose={() => setOpened(null)}
      />}
    </AnimatePresence>
  </section>;
}

/** Кто снимается в конкретном исполнении — выбор из кастинга гипотезы. */
export function CreativeCasting({ creative, family }: { creative: Creative; family: Hypothesis }) {
  const { data, act } = useAppState();
  const [opened, setOpened] = useState<Character | null>(null);
  const [busy, setBusy] = useState(false);
  const editable = canCast(data.user.role);
  const current = family.characters.find((character) => character.id === creative.characterId) ?? null;
  const choose = async (value: string) => {
    setBusy(true);
    try {
      await act(`/api/creatives/${creative.id}/variation`, {
        method: "PATCH", body: JSON.stringify({ characterId: value || null })
      }, value ? "Персонаж назначен" : "Персонаж снят с крео");
    } finally { setBusy(false); }
  };
  if (!family.characters.length && !current) {
    return <div className="creative-casting creative-casting--empty">
      <span>Персонаж</span>
      <small>Кастинга у гипотезы пока нет — прикрепите его в карточке гипотезы.</small>
    </div>;
  }
  return <div className="creative-casting">
    {current && <button type="button" className="creative-casting__face" onClick={() => setOpened(current)}
      aria-label={`Открыть лист персонажа ${current.name}`} disabled={!current.image}>
      <CharacterAvatar character={current} size="md" />
    </button>}
    <div className="creative-casting__control">
      <span>Персонаж</span>
      {editable ? <Select value={creative.characterId ?? ""} disabled={busy}
        onChange={(event) => choose(event.target.value)} aria-label="Кто снимается в этом крео">
        <option value="">не назначен</option>
        {family.characters.map((character) => <option key={character.id} value={character.id}>{character.name}</option>)}
      </Select> : <strong>{current?.name ?? "не назначен"}</strong>}
    </div>
    <AnimatePresence>
      {opened?.image && <Lightbox
        src={opened.image.url}
        alt={`Character sheet ${opened.name}`}
        title={opened.name}
        caption={opened.description}
        onClose={() => setOpened(null)}
      />}
    </AnimatePresence>
  </div>;
}
